import type { NxOfficeIconName } from '../OfficeRibbon';

export type NxRichTextMark = 'bold' | 'italic' | 'underline' | 'strike' | 'sup' | 'sub';
export type NxRichTextAlign = 'left' | 'center' | 'right' | 'justify';
export type NxRichTextList = 'bulletList' | 'orderedList';
export type NxRichTextPasteMode = 'ask' | 'source' | 'destination' | 'text';
/** How a selected picture sits in the text: in the line, or floated to one side. */
export type NxRichTextImageWrap = 'inline' | 'left' | 'right';

export interface NxRichTextFontFamily {
  value: string;
  label: string;
}

/**
 * What the editor behind the toolbar reports about the caret or selection.
 * The toolbar only shows it; it never reads the document itself.
 */
export interface NxRichTextToolbarState {
  marks: Partial<Record<NxRichTextMark | 'link', boolean>>;
  align?: NxRichTextAlign;
  list?: NxRichTextList | null;
  /** 0 is body text, 1–3 the heading levels. */
  heading: number;
  fontFamily: string;
  fontSize: number;
  /** Value of the text colour picker. */
  color: string;
  /** Colour shown under the text-colour icon; empty when the text has none of its own. */
  explicitColor?: string;
  highlight: string;
  /** Line spacing in percent (100 = single); `null` is the editor's default. */
  lineSpacing?: number | null;
  canUndo: boolean;
  canRedo: boolean;
  hasSelection: boolean;
  inTable: boolean;
  /** The selected picture, `null` when none is selected. */
  image?: { wrap: NxRichTextImageWrap } | null;
  /** The link under the caret, offered when the link popover opens. */
  linkHref?: string;
  pasteMode: NxRichTextPasteMode;
  /** A PDF export is running. */
  pdfBusy?: boolean;
  /** The editor is busy with media; PDF export waits. */
  busy?: boolean;
}

export type NxRichTextCommand =
  | { id: 'undo' }
  | { id: 'redo' }
  | { id: 'copy' }
  | { id: 'cut' }
  | { id: 'paste' }
  | { id: 'mark'; mark: NxRichTextMark }
  | { id: 'clearFormatting' }
  | { id: 'align'; align: NxRichTextAlign }
  | { id: 'list'; list: NxRichTextList }
  | { id: 'indent'; delta: 1 | -1 }
  | { id: 'heading'; level: number }
  | { id: 'fontFamily'; family: string }
  | { id: 'fontSize'; size: number }
  | { id: 'color'; color: string }
  | { id: 'highlight'; color: string }
  | { id: 'lineSpacing'; spacing: number | null }
  | { id: 'link'; href: string }
  | { id: 'unlink' }
  | { id: 'table'; rows: number; columns: number }
  | { id: 'image' }
  | { id: 'quote'; delta: 1 | -1 }
  | { id: 'divider' }
  | { id: 'pdf' }
  | { id: 'find' }
  | { id: 'replace' }
  | { id: 'pasteMode'; mode: NxRichTextPasteMode }
  | { id: 'tableRow'; op: 'insert' | 'delete' }
  | { id: 'tableColumn'; op: 'insert' | 'delete' }
  | { id: 'tableMerge' }
  | { id: 'tableSplit' }
  | { id: 'imageWrap'; wrap: NxRichTextImageWrap }
  | { id: 'imageEdit' }
  | { id: 'imageDelete' };

export type NxRichTextCommandId = NxRichTextCommand['id'];

/**
 * How the toolbar is laid out: `compact` is one row of the common tools with
 * the rest under „⋯“; `full` shows every tool in two rows.
 */
export type NxRichTextToolbarLayout = 'compact' | 'full';

/** The groups of the compact row; a group left out moves under „⋯“. */
export type NxRichTextToolbarGroup = 'history' | 'font' | 'marks' | 'paragraph' | 'insert' | 'find';

export const NX_RICH_TEXT_TOOLBAR_GROUPS: readonly NxRichTextToolbarGroup[] = [
  'history',
  'font',
  'marks',
  'paragraph',
  'insert',
  'find',
];

/** What the compact row shows unless the host says otherwise: all but find. */
export const NX_RICH_TEXT_TOOLBAR_DEFAULT_GROUPS: Readonly<
  Record<NxRichTextToolbarGroup, boolean>
> = {
  history: true,
  font: true,
  marks: true,
  paragraph: true,
  insert: true,
  find: false,
};

/** A host's own entry under „⋯“ (Mail: a picture by address, a comment). */
export interface NxRichTextExtraAction {
  id: string;
  label: string;
  icon?: NxOfficeIconName;
  /** Shown beside the label, e.g. `F6`. */
  shortcut?: string;
  disabled?: boolean;
}
